/**
 * Contrato da API de analytics (GET /api/v2/domains/:id/analytics/<x>).
 * Espelha apps/server/src/analytics/query/* — manter os dois em sincronia.
 *
 * Parâmetros comuns (query string):
 *   from, to      ISO 8601 (to exclusivo). Padrão: últimos 7 dias.
 *   tz            fuso IANA (padrão America/Sao_Paulo) — buckets e "hoje".
 *   interval      hour | day | week | month (padrão: escolhido pelo tamanho do período).
 *   compare=1     inclui o período anterior de mesmo tamanho (`previous`).
 *   f.<dim>=<v>   filtros (repetível; valores da mesma dimensão = OU, dimensões diferentes = E).
 *   includeBots=1 inclui robôs nas métricas de visitantes (padrão: excluídos).
 *
 * Plano: analytics_basic → overview, timeseries (visitors|pageviews|requests),
 * breakdown (path|source|country), até 30 dias, sem filtros/compare/export.
 * analytics_advanced → tudo, até 13 meses. Fora do plano: 403 feature_not_in_plan.
 */

export type Interval = "hour" | "day" | "week" | "month";

export const DIMENSIONS = [
  "path",
  "entry",
  "exit",
  "country",
  "region",
  "city",
  "device",
  "browser",
  "os",
  "source",
  "channel",
  "referrer",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "lang",
  "bot",
  "status_class",
  "content_kind",
] as const;
export type Dimension = (typeof DIMENSIONS)[number];

/** Dimensões liberadas no analytics_basic (breakdown). */
export const BASIC_DIMENSIONS: readonly Dimension[] = ["path", "source", "country"];

export const TIMESERIES_METRICS = ["visitors", "pageviews", "requests", "bytes", "errors", "latency_p95", "sessions"] as const;
export type TimeseriesMetric = (typeof TIMESERIES_METRICS)[number];
export const BASIC_TIMESERIES_METRICS: readonly TimeseriesMetric[] = ["visitors", "pageviews", "requests"];

export type BreakdownMetric = "visitors" | "pageviews" | "requests";

export type Filters = Partial<Record<Dimension, string[]>>;

export type AnalyticsQuery = {
  from: string;
  to: string;
  tz?: string;
  interval?: Interval;
  compare?: boolean;
  filters?: Filters;
  includeBots?: boolean;
};

/** Metadados que todas as respostas trazem. */
export type Meta = {
  from: string;
  to: string;
  tz: string;
  interval: Interval;
  /** De onde vêm visitantes/pageviews: beacon (navegador) ou requisições HTML do edge. */
  pageviewSource: "beacon" | "edge";
  /** analytics_basic | analytics_advanced */
  tier: "basic" | "advanced";
};

export type OverviewNumbers = {
  visitors: number;
  pageviews: number;
  sessions: number;
  /** 0..1 */
  bounceRate: number;
  avgDurationMs: number;
  requests: number;
  bytes: number;
  /** 0..1 (respostas 5xx / requisições) */
  errorRate: number;
  p50Ms: number;
  p95Ms: number;
};
export type Overview = OverviewNumbers & { previous?: OverviewNumbers; meta: Meta };

export type Point = { t: string; v: number };
export type Timeseries = { metric: TimeseriesMetric; points: Point[]; previous?: Point[]; meta: Meta };

export type BreakdownItem = { key: string; visitors: number; pageviews: number; requests: number; share: number };
export type Breakdown = { dimension: Dimension; metric: BreakdownMetric; items: BreakdownItem[]; meta: Meta };

export type PagesKind = "top" | "entry" | "exit";
export type PageItem = {
  path: string;
  visitors: number;
  pageviews: number;
  /** top: tempo médio na página (beacon "leave"); null sem dados. */
  avgTimeMs: number | null;
  /** entry/exit: sessões que começaram/terminaram na página. */
  sessions: number;
  /** entry: taxa de rejeição das sessões que começaram aqui; exit: sessões que saíram / pageviews. */
  rate: number | null;
};
export type Pages = { kind: PagesKind; items: PageItem[]; meta: Meta };

export type VitalName = "lcp" | "inp" | "cls" | "fcp" | "ttfb";
export type VitalStat = { p75: number | null; good: number; needsImprovement: number; poor: number };
export type VitalsRow = { key: string; samples: number } & Record<VitalName, number | null>;
export type Vitals = {
  groupBy: "path" | "device";
  summary: Record<VitalName, VitalStat> & { samples: number };
  items: VitalsRow[];
  meta: Meta;
};

export type BotItem = { bot: string; verified: boolean; requests: number; pages: number; errors: number; avgMs: number; lastSeen: string };
export type Bots = { items: BotItem[]; meta: Meta };
export type BotPageItem = { path: string; hits: number; lastCrawl: string; errors: number; avgMs: number };
export type BotPages = { bot: string | null; items: BotPageItem[]; meta: Meta };

/** Tipo de ataque (cenário do CrowdSec) no período. */
export type SecurityScenario = { scenario: string; kind: string; message: string; attacks: number; ips: number; lastSeen: string };
/** IP (ou faixa) que mais atacou; `banned` = banimento ainda valendo. */
export type SecurityIp = { ip: string; country: string; asName: string; attacks: number; lastSeen: string; banned: boolean; scenarios: string[] };
export type SecurityEvent = {
  id: number;
  at: string;
  kind: string;
  scenario: string;
  message: string;
  ip: string;
  country: string;
  method: string;
  uri: string;
  decision: string;
};

/**
 * Segurança: requisições barradas pelo edge (WAF, IP banido, rate limit) + alertas do CrowdSec do domínio.
 * Os alertas respeitam só o período (os filtros do dashboard são de visitas).
 */
export type Security = {
  summary: { blocked: number; flagged: number; attacks: number; ips: number; bans: number; activeBans: number };
  timeline: { t: string; blocked: number; attacks: number }[];
  byScenario: SecurityScenario[];
  topIps: SecurityIp[];
  recent: SecurityEvent[];
  byAction: { action: string; requests: number }[];
  byCountry: { country: string; requests: number }[];
  byPath: { path: string; requests: number }[];
  meta: Meta;
};

export type ErrorItem = { path: string; status: number; count: number; lastSeen: string };
export type Errors = { items: ErrorItem[]; meta: Meta };

export type Realtime = {
  visitors5m: number;
  pageviews5m: number;
  pages: { path: string; visitors: number }[];
  sources: { source: string; visitors: number }[];
};

export type TailorkitMiss = { path: string; version: number; ruleId: string; misses: number; lastSeen: string };
export type TailorkitMisses = { items: TailorkitMiss[]; meta: Meta };

/**
 * GET /api/v2/analytics/sparklines?domainIds=a,b,c (máx. 100, só domínios da org).
 * Visitantes por dia nos últimos 7 dias (fuso tz, padrão America/Sao_Paulo), a partir dos rollups.
 * Domínios sem dados vêm com zeros; org sem analytics → 403 feature_not_in_plan.
 */
export type Sparklines = { items: Record<string, { visitors7d: number; points: number[] }> };
